import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  useDossierDetail, 
  useGenerateDossierPdf, 
  useUploadDocument, 
  useReviewDocument,
  useAppointments,
  useCreateAppointment,
  useDossierAuditLogs
} from '../../lib/hooks';
import { useAuth } from '../../contexts/AuthContext';

// Ouvre un document sécurisé avec le token JWT
const handleViewDocument = async (documentId) => {
  try {
    const token = localStorage.getItem('hav_token');
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/documents/${documentId}/file`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('Erreur');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch {
    toast.error('Impossible de charger le document');
  }
};
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetFooter } from '../../components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { 
  ArrowLeft, 
  Loader2, 
  Download,
  FileText,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Calendar,
  MapPin,
  Plus,
  Eye,
  ChevronDown,
  ChevronUp,
  History,
  FileCheck,
  User,
  Trash2,
  Settings2
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const visaLabelsFn = (lang = 'fr') => ({
  studies: lang === 'de' ? 'Studium' : 'Études',
  formation_pro: lang === 'de' ? 'Berufsausbildung' : 'Formation Pro',
  work: lang === 'de' ? 'Arbeit' : 'Travail',
  family_reunification: lang === 'de' ? 'Familienzusammenführung' : 'Regroupement familial',
});

const statusConfigFn = (lang = 'fr') => ({
  missing: { label: lang === 'de' ? 'Fehlend' : 'Manquant', color: 'bg-slate-100 text-slate-600', icon: AlertCircle },
  pending_review: { label: lang === 'de' ? 'Ausstehend' : 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  validated: { label: lang === 'de' ? 'Bestätigt' : 'Validé', color: 'bg-success/10 text-success', icon: CheckCircle },
  rejected: { label: lang === 'de' ? 'Abgelehnt' : 'Rejeté', color: 'bg-destructive/10 text-destructive', icon: XCircle },
});

const appointmentStatusConfigFn = (lang = 'fr') => ({
  scheduled: { label: lang === 'de' ? 'Geplant' : 'Planifié', color: 'bg-blue-100 text-blue-700' },
  completed: { label: lang === 'de' ? 'Abgeschlossen' : 'Terminé', color: 'bg-success/10 text-success' },
  to_reschedule: { label: lang === 'de' ? 'Neu planen' : 'À replanifier', color: 'bg-orange-100 text-orange-700' },
});

const DossierDetail = () => {
  const { studentId } = useParams();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [rejectSheetOpen, setRejectSheetOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [validateDialogOpen, setValidateDialogOpen] = useState(false);
  const [appointmentSheetOpen, setAppointmentSheetOpen] = useState(false);
  const [newAppointment, setNewAppointment] = useState({ date: '', location: '', notes: '' });
  const [expandedDocs, setExpandedDocs] = useState({});
  const [auditLogsOpen, setAuditLogsOpen] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [manageDocsOpen, setManageDocsOpen] = useState(false);
  const [addDocLabel, setAddDocLabel] = useState('');
  const [addDocFormat, setAddDocFormat] = useState('pdf');
  const [addDocNotes, setAddDocNotes] = useState('');
  const [addDocTemplate, setAddDocTemplate] = useState(null);
  const [addingDoc, setAddingDoc] = useState(false);
  const [docRequests, setDocRequests] = useState([]);
  const [loadingDocReqs, setLoadingDocReqs] = useState(false);

  // RBAC
  const isAdmin = user?.role === 'admin';
  const isRespAdmin = user?.role === 'resp_admin';
  const canReview = isAdmin || isRespAdmin;

  // Localized maps
  const visaLabels = visaLabelsFn(lang);
  const statusConfig = statusConfigFn(lang);
  const appointmentStatusConfig = appointmentStatusConfigFn(lang);

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const fetchDocRequests = useCallback(async () => {
    try {
      setLoadingDocReqs(true);
      const res = await axios.get(`${API_URL}/dossiers/${studentId}/document-requests`);
      setDocRequests(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingDocReqs(false); }
  }, [studentId, API_URL]);

  const initDocRequests = async () => {
    try {
      await axios.post(`${API_URL}/dossiers/${studentId}/document-requests`);
      toast.success('Liste de documents initialisée');
      fetchDocRequests();
      refetch();
    } catch (e) {
      if (e.response?.status === 400) {
        toast.info('Les documents requis existent déjà');
      } else {
        toast.error("Erreur lors de l'initialisation");
      }
    }
  };

  const handleAddDocRequest = async () => {
    if (!addDocLabel.trim()) return;
    try {
      setAddingDoc(true);
      const formData = new FormData();
      formData.append('label', addDocLabel);
      formData.append('format', addDocFormat);
      if (addDocNotes) formData.append('notes', addDocNotes);
      if (addDocTemplate) formData.append('template', addDocTemplate);
      
      await axios.post(`${API_URL}/dossiers/${studentId}/document-requests/add`, formData);
      toast.success(`Document "${addDocLabel}" ajouté`);
      setAddDocLabel('');
      setAddDocFormat('pdf');
      setAddDocNotes('');
      setAddDocTemplate(null);
      fetchDocRequests();
      refetch();
    } catch (e) { toast.error("Erreur lors de l'ajout"); }
    finally { setAddingDoc(false); }
  };

  const handleDeleteDocRequest = async (reqId, label) => {
    try {
      await axios.delete(`${API_URL}/dossiers/${studentId}/document-requests/${reqId}`);
      toast.success(`"${label}" retiré de la liste`);
      fetchDocRequests();
      refetch();
    } catch (e) { toast.error("Erreur lors de la suppression"); }
  };

  const handleUploadTemplate = async (reqId, file) => {
    try {
      const formData = new FormData();
      formData.append('template', file);
      const res = await axios.post(`${API_URL}/dossiers/${studentId}/document-requests/${reqId}/template`, formData);
      toast.success('Modèle uploadé');
      fetchDocRequests();
      return res.data;
    } catch (e) { toast.error("Erreur lors de l'upload"); }
  };

  // Queries
  const { data: dossier, isLoading, error } = useDossierDetail(studentId);
  const { data: appointments } = useAppointments(studentId);
  const { data: auditLogs } = useDossierAuditLogs(studentId);

  // Mutations
  const generatePdf = useGenerateDossierPdf(studentId);
  const uploadDocument = useUploadDocument(studentId);
  const reviewDocument = useReviewDocument(studentId);
  const createAppointment = useCreateAppointment(studentId);

  const handleGeneratePdf = async () => {
    try {
      await generatePdf.mutateAsync();
      toast.success('Dossier PDF généré avec succès');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    }
  };

  const handleValidate = async () => {
    if (!selectedDoc) return;
    try {
      await reviewDocument.mutateAsync({
        documentId: selectedDoc.document.id,
        status: 'validated',
      });
      toast.success('Document validé avec succès');
      setValidateDialogOpen(false);
      setSelectedDoc(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  const handleReject = async () => {
    if (!selectedDoc || !rejectComment.trim()) {
      toast.error('Le motif du rejet est requis');
      return;
    }
    try {
      await reviewDocument.mutateAsync({
        documentId: selectedDoc.document.id,
        status: 'rejected',
        comment: rejectComment,
      });
      toast.success('Document rejeté');
      setRejectSheetOpen(false);
      setRejectComment('');
      setSelectedDoc(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du rejet');
    }
  };

  const handleCreateAppointment = async () => {
    if (!newAppointment.date || !newAppointment.location) {
      toast.error('Date et lieu requis');
      return;
    }
    try {
      await createAppointment.mutateAsync({
        student_id: studentId,
        ...newAppointment,
        type: 'rendez_vous',
      });
      toast.success('Rendez-vous créé');
      setAppointmentSheetOpen(false);
      setNewAppointment({ date: '', location: '', notes: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const onDrop = useCallback(async (acceptedFiles, docKey) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    
    // Validate file
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non autorisé. Utilisez PDF, JPG ou PNG.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux. Maximum 10MB.');
      return;
    }
    
    setUploadingDoc(docKey);
    try {
      await uploadDocument.mutateAsync({
        documentType: docKey,
        file,
      });
      toast.success('Document téléchargé avec succès');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setUploadingDoc(null);
    }
  }, [uploadDocument]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(lang === 'de' ? 'de-DE' : 'fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString(lang === 'de' ? 'de-DE' : 'fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !dossier) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">{lang === 'de' ? 'Akte nicht gefunden' : 'Dossier non trouvé'}</h2>
        <Button variant="outline" onClick={() => navigate('/dossiers')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {lang === 'de' ? 'Zurück' : 'Retour'}
        </Button>
      </div>
    );
  }

  const isComplete = dossier.status === 'complete' || dossier.status === 'pdf_generated';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dossier-detail-page">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate('/dossiers')} className="mb-2">
        <ArrowLeft className="w-4 h-4 mr-2" />
        {lang === 'de' ? 'Zurück zu den Akten' : 'Retour aux dossiers'}
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Student Info */}
        <div className="space-y-6">
          {/* Student Card */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="text-center">
              <Avatar className="h-20 w-20 mx-auto mb-2">
                <AvatarFallback className="bg-primary text-white text-2xl">
                  {dossier.student_name.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <CardTitle className="font-heading">{dossier.student_name}</CardTitle>
              <CardDescription>{dossier.student_email}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('students_level', lang)}</span>
                <Badge variant="outline">{dossier.student_level}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('dossier_detail_visa', lang)}</span>
                <Badge className="bg-secondary/10 text-secondary">
                  {visaLabels[dossier.visa_objective]}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{lang === 'de' ? 'Studierendenstatus' : 'Statut étudiant'}</span>
                {canReview ? (
                  <select
                    value={dossier.enrollment_status || 'pending'}
                    onChange={async (e) => {
                      try {
                        const { studentsApi } = await import('../../lib/api');
                        await studentsApi.updateStatus(studentId, e.target.value);
                        toast.success(lang === 'de' ? 'Status aktualisiert' : 'Statut mis à jour');
                      } catch (err) {
                        toast.error(lang === 'de' ? 'Fehler beim Aktualisieren des Status' : "Erreur lors de la mise à jour du statut");
                      }
                    }}
                    className="text-sm border rounded-md px-2 py-1 cursor-pointer"
                    data-testid="dossier-enrollment-status-select"
                  >
                    <option value="active">{lang === 'de' ? 'Aktiv' : 'Actif'}</option>
                    <option value="pending">{lang === 'de' ? 'Ausstehend' : 'En attente'}</option>
                    <option value="completed">{lang === 'de' ? 'Abgeschlossen' : 'Terminé'}</option>
                    <option value="suspended">{lang === 'de' ? 'Gesperrt' : 'Suspendu'}</option>
                  </select>
                ) : (
                  <Badge className={
                    dossier.enrollment_status === 'active' ? 'bg-success/10 text-success' :
                    dossier.enrollment_status === 'completed' ? 'bg-blue-100 text-blue-700' :
                    dossier.enrollment_status === 'suspended' ? 'bg-destructive/10 text-destructive' :
                    'bg-yellow-100 text-yellow-700'
                  }>
                    {dossier.enrollment_status === 'active' ? (lang === 'de' ? 'Aktiv' : 'Actif') :
                     dossier.enrollment_status === 'completed' ? (lang === 'de' ? 'Abgeschlossen' : 'Terminé') :
                     dossier.enrollment_status === 'suspended' ? (lang === 'de' ? 'Gesperrt' : 'Suspendu') : (lang === 'de' ? 'Ausstehend' : 'En attente')}
                  </Badge>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{lang === 'de' ? 'Akte' : 'Dossier'}</span>
                <Badge className={
                  dossier.status === 'complete' || dossier.status === 'pdf_generated' ? 'bg-success/10 text-success' :
                  dossier.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-slate-100 text-slate-600'
                }>
                  {dossier.status === 'complete' ? (lang === 'de' ? 'Vollständig' : 'Complet') :
                   dossier.status === 'pdf_generated' ? (lang === 'de' ? 'PDF erstellt' : 'PDF généré') :
                   dossier.status === 'in_progress' ? (lang === 'de' ? 'In Bearbeitung' : 'En cours') : (lang === 'de' ? 'Unvollständig' : 'Incomplet')}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Completion Ring */}
          <Card className="border-0 shadow-sm">
            <CardContent className="py-6">
              <div className="relative w-32 h-32 mx-auto">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="12"
                    className="text-muted"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="12"
                    strokeDasharray={`${dossier.completion_percentage * 3.52} 352`}
                    className="text-primary"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold font-heading">
                    {Math.round(dossier.completion_percentage)}%
                  </span>
                </div>
              </div>
              <p className="text-center text-muted-foreground mt-4">
                {dossier.validated_count} / {dossier.total_required} {lang === 'de' ? 'bestätigte Dokumente' : 'documents validés'}
              </p>
            </CardContent>
          </Card>

          {/* Generate PDF Button */}
          {canReview && (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-4">
                {dossier.final_pdf_url ? (
                  <Button className="w-full" asChild>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${dossier.final_pdf_url}`} target="_blank" rel="noopener noreferrer">
                      <Download className="w-4 h-4 mr-2" />
                      {lang === 'de' ? 'Endakte herunterladen' : 'Télécharger le dossier final'}
                    </a>
                  </Button>
                ) : (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Button 
                            className="w-full" 
                            onClick={handleGeneratePdf}
                            disabled={!isComplete || generatePdf.isPending}
                          >
                            {generatePdf.isPending ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <FileCheck className="w-4 h-4 mr-2" />
                            )}
                            {lang === 'de' ? 'Endgültiges PDF erstellen' : 'Générer le PDF final'}
                          </Button>
                        </div>
                      </TooltipTrigger>
                      {!isComplete && (
                        <TooltipContent>
                          <p>{lang === 'de' ? 'Alle Dokumente müssen bestätigt sein' : 'Tous les documents doivent être validés'}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                )}
              </CardContent>
            </Card>
          )}

          {/* Appointments */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-secondary" />
                  {lang === 'de' ? 'Termine' : 'Rendez-vous'}
                </CardTitle>
                {canReview && (
                  <Button size="sm" variant="outline" onClick={() => setAppointmentSheetOpen(true)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {appointments?.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">{lang === 'de' ? 'Keine Termine' : 'Aucun rendez-vous'}</p>
              ) : (
                <div className="space-y-3">
                  {appointments?.map((appt) => (
                    <div key={appt.id} className="flex items-start gap-3 p-3 rounded-lg border">
                      <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-secondary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{formatDate(appt.date)}</p>
                        <p className="text-xs text-muted-foreground">{appt.location}</p>
                        <Badge className={appointmentStatusConfig[appt.status]?.color + ' mt-1'}>
                          {appointmentStatusConfig[appt.status]?.label}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Documents Checklist */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    {lang === 'de' ? 'Erforderliche Dokumente' : 'Documents requis'}
                  </CardTitle>
                  <CardDescription>
                    {lang === 'de' ? 'Dokumenten-Checkliste für Visum' : 'Checklist des documents pour visa'} {visaLabels[dossier.visa_objective]}
                  </CardDescription>
                </div>
                {canReview && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { 
                      e.stopPropagation();
                      e.preventDefault();
                      setManageDocsOpen(true);
                      fetchDocRequests().catch(console.error);
                    }}
                    data-testid="manage-docs-btn"
                  >
                    <Settings2 className="w-4 h-4 mr-2" />
                    {lang === 'de' ? 'Liste verwalten' : 'Gérer la liste'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dossier.documents.map((doc) => {
                  const StatusIcon = statusConfig[doc.status]?.icon || AlertCircle;
                  const isExpanded = expandedDocs[doc.key];
                  
                  return (
                    <div key={doc.key} className="border rounded-lg overflow-hidden">
                      {/* Document Row */}
                      <div 
                        className={`flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 ${doc.document ? '' : 'bg-muted/30'}`}
                        onClick={() => setExpandedDocs({...expandedDocs, [doc.key]: !isExpanded})}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            doc.status === 'validated' ? 'bg-success/10' :
                            doc.status === 'rejected' ? 'bg-destructive/10' :
                            doc.status === 'pending_review' ? 'bg-yellow-100' :
                            'bg-muted'
                          }`}>
                            <StatusIcon className={`w-5 h-5 ${
                              doc.status === 'validated' ? 'text-success' :
                              doc.status === 'rejected' ? 'text-destructive' :
                              doc.status === 'pending_review' ? 'text-yellow-600' :
                              'text-muted-foreground'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium">{doc.label}</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              {doc.format && (
                                <span className="text-xs text-muted-foreground uppercase bg-muted px-1.5 py-0.5 rounded">
                                  {doc.format}
                                </span>
                              )}
                              {doc.document && (
                                <span className="text-xs text-muted-foreground">
                                  {lang === 'de' ? 'Eingereicht am' : 'Déposé le'} {formatDate(doc.document.uploaded_at)}
                                </span>
                              )}
                              {doc.notes && (
                                <span className="text-xs text-amber-600" title={doc.notes}>
                                  {lang === 'de' ? 'Admin-Notiz' : 'Note admin'}
                                </span>
                              )}
                            </div>
                            {doc.template_url && (
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}${doc.template_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline flex items-center gap-1 mt-0.5"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="w-3 h-3" />
                                {doc.template_name || (lang === 'de' ? 'Vorlage herunterladen' : 'Télécharger le modèle')}
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={statusConfig[doc.status]?.color}>
                            {statusConfig[doc.status]?.label}
                          </Badge>
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="p-4 border-t bg-muted/20">
                          {doc.status === 'missing' && (
                            <DropzoneArea 
                              docKey={doc.key} 
                              onDrop={onDrop} 
                              isUploading={uploadingDoc === doc.key}
                            />
                          )}
                          
                          {doc.status === 'rejected' && doc.document && (
                            <div className="space-y-4">
                              <div className="p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                                <p className="text-sm font-medium text-destructive">{lang === 'de' ? 'Ablehnungsgrund:' : 'Motif du rejet :'}</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {doc.document.review_comment || (lang === 'de' ? 'Nicht angegeben' : 'Non spécifié')}
                                </p>
                              </div>
                              <DropzoneArea 
                                docKey={doc.key} 
                                onDrop={onDrop} 
                                isUploading={uploadingDoc === doc.key}
                                label={lang === 'de' ? 'Dokument erneut einreichen' : 'Re-déposer le document'}
                              />
                            </div>
                          )}

                          {doc.status === 'pending_review' && doc.document && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-4">
                                <Button variant="outline" onClick={() => handleViewDocument(doc.document.id)}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Voir le document
                                </Button>
                                {canReview && (
                                  <>
                                    <Button 
                                      className="bg-success hover:bg-success/90"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedDoc(doc);
                                        setValidateDialogOpen(true);
                                      }}
                                    >
                                      <CheckCircle className="w-4 h-4 mr-2" />
                                      {lang === 'de' ? 'Bestätigen' : 'Valider'}
                                    </Button>
                                    <Button 
                                      variant="destructive"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedDoc(doc);
                                        setRejectSheetOpen(true);
                                      }}
                                    >
                                      <XCircle className="w-4 h-4 mr-2" />
                                      {lang === 'de' ? 'Ablehnen' : 'Rejeter'}
                                    </Button>
                                  </>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                <p>{lang === 'de' ? 'Eingereicht von' : 'Déposé par'}: {doc.document.uploaded_by_name}</p>
                                <p>{lang === 'de' ? 'Größe' : 'Taille'}: {(doc.document.file_size / 1024).toFixed(1)} KB</p>
                              </div>
                            </div>
                          )}

                          {doc.status === 'validated' && doc.document && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-4">
                                <Button variant="outline" onClick={() => handleViewDocument(doc.document.id)}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  {lang === 'de' ? 'Dokument ansehen' : 'Voir le document'}
                                </Button>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-success">
                                <CheckCircle className="w-4 h-4" />
                                <span>{lang === 'de' ? 'Bestätigt am' : 'Validé le'} {formatDate(doc.document.reviewed_at)} {lang === 'de' ? 'von' : 'par'} {doc.document.reviewed_by_name}</span>
                              </div>
                            </div>
                          )}

                          {/* History */}
                          {doc.history?.length > 0 && (
                            <div className="mt-4 pt-4 border-t">
                              <p className="text-sm font-medium text-muted-foreground mb-2">
                                {lang === 'de' ? 'Verlauf' : 'Historique'} ({doc.history.length} {doc.history.length > 1 ? (lang === 'de' ? 'frühere Versionen' : 'versions précédentes') : (lang === 'de' ? 'frühere Version' : 'version précédente')})
                              </p>
                              <div className="space-y-2">
                                {doc.history.slice(0, 3).map((hist) => (
                                  <div key={hist.id} className="flex items-center justify-between text-sm p-2 bg-muted/50 rounded">
                                    <span className="text-muted-foreground">
                                      {formatDateTime(hist.uploaded_at)}
                                    </span>
                                    <Badge variant="outline" className="text-xs">
                                      {hist.status === 'rejected' ? (lang === 'de' ? 'Abgelehnt' : 'Rejeté') : hist.status}
                                    </Badge>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Audit Trail */}
          <Collapsible open={auditLogsOpen} onOpenChange={setAuditLogsOpen}>
            <Card className="border-0 shadow-sm">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <History className="w-5 h-5 text-muted-foreground" />
                    {lang === 'de' ? 'Aktionsverlauf' : 'Historique des actions'}
                  </CardTitle>
                  {auditLogsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  {auditLogs?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">{lang === 'de' ? 'Keine Aktionen aufgezeichnet' : 'Aucune action enregistrée'}</p>
                  ) : (
                    <div className="space-y-3">
                      {auditLogs?.slice(0, 30).map((log) => (
                        <div key={log.id} className="flex items-start gap-3 p-3 border-l-2 border-muted">
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                            <User className="w-4 h-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm">{log.details}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {log.actor_name} • {formatDateTime(log.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>
      </div>

      {/* Validate Dialog */}
      <AlertDialog open={validateDialogOpen} onOpenChange={setValidateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la validation</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir valider le document "{selectedDoc?.label}" ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('action_cancel', lang)}</AlertDialogCancel>
            <AlertDialogAction onClick={handleValidate} className="bg-success hover:bg-success/90">
              Valider
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Sheet */}
      <Sheet open={rejectSheetOpen} onOpenChange={setRejectSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle className="font-heading">{lang === 'de' ? 'Dokument ablehnen' : 'Rejeter le document'}</SheetTitle>
            <SheetDescription>
              {lang === 'de' ? `Geben Sie den Ablehnungsgrund für "${selectedDoc?.label}" an` : `Indiquez le motif du rejet pour "${selectedDoc?.label}"`}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            {selectedDoc?.document && (
              <Button variant="outline" className="w-full" onClick={() => handleViewDocument(selectedDoc.document.id)}>
                <Eye className="w-4 h-4 mr-2" />
                {lang === 'de' ? 'Dokument ansehen' : 'Voir le document'}
              </Button>
            )}
            <div className="space-y-2">
              <Label>{lang === 'de' ? 'Ablehnungsgrund' : 'Motif du rejet'} *</Label>
              <Textarea
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder={lang === 'de' ? 'Erklären Sie, warum dieses Dokument abgelehnt wird...' : 'Expliquez pourquoi ce document est rejeté...'}
                rows={4}
              />
            </div>
          </div>
          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setRejectSheetOpen(false)}>
              {lang === 'de' ? 'Abbrechen' : 'Annuler'}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={reviewDocument.isPending}
            >
              {reviewDocument.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                lang === 'de' ? 'Ablehnung bestätigen' : 'Confirmer le rejet'
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Appointment Sheet */}
      <Sheet open={appointmentSheetOpen} onOpenChange={setAppointmentSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle className="font-heading">{lang === 'de' ? 'Neuer Termin' : 'Nouveau rendez-vous'}</SheetTitle>
            <SheetDescription>
              {lang === 'de' ? 'Termin planen' : 'Planifier un rendez-vous'}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>{lang === 'de' ? 'Datum und Uhrzeit' : 'Date et heure'} *</Label>
              <Input
                type="datetime-local"
                value={newAppointment.date}
                onChange={(e) => setNewAppointment({...newAppointment, date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>{lang === 'de' ? 'Ort' : 'Lieu'} *</Label>
              <Input
                value={newAppointment.location}
                onChange={(e) => setNewAppointment({...newAppointment, location: e.target.value})}
                placeholder={lang === 'de' ? 'Z.B.: Deutsche Botschaft, Paris' : "Ex: Ambassade d'Allemagne, Paris"}
              />
            </div>
            <div className="space-y-2">
              <Label>{lang === 'de' ? 'Notizen' : 'Notes'}</Label>
              <Textarea
                value={newAppointment.notes}
                onChange={(e) => setNewAppointment({...newAppointment, notes: e.target.value})}
                placeholder={lang === 'de' ? 'Zusätzliche Notizen...' : 'Notes supplémentaires...'}
                rows={3}
              />
            </div>
          </div>
          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setAppointmentSheetOpen(false)}>
              {lang === 'de' ? 'Abbrechen' : 'Annuler'}
            </Button>
            <Button onClick={handleCreateAppointment} disabled={createAppointment.isPending}>
              {createAppointment.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                lang === 'de' ? 'Termin erstellen' : 'Créer le rendez-vous'
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Manage Document Requests Sheet */}
      {manageDocsOpen && (
        <Sheet open={manageDocsOpen} onOpenChange={setManageDocsOpen}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Gérer les documents requis</SheetTitle>
            </SheetHeader>
          
            {/* Existing document requests */}
            <div className="space-y-2 mt-4">
              {docRequests.length === 0 && !loadingDocReqs && (
                <div className="text-center py-6">
                  <FileText className="w-10 h-10 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">Aucun document personnalisé défini</p>
                  <Button onClick={initDocRequests} size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    {lang === 'de' ? 'Aus Standardliste initialisieren' : 'Initialiser depuis la liste par défaut'}
                  </Button>
                </div>
              )}
              
              {docRequests.map((req) => (
                <div key={req.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{req.label}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground uppercase bg-muted px-1.5 py-0.5 rounded">{req.format}</span>
                      {req.template_name && (
                        <span className="text-xs text-primary">{lang === 'de' ? 'Vorlage' : 'Modèle'}: {req.template_name}</span>
                      )}
                      {req.notes && (
                        <span className="text-xs text-amber-600 truncate max-w-[150px]" title={req.notes}>{req.notes}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleUploadTemplate(req.id, e.target.files[0]);
                            e.target.value = '';
                          }
                        }}
                      />
                      <span className="inline-flex items-center justify-center h-8 w-8 rounded-md border hover:bg-muted cursor-pointer" title={lang === 'de' ? 'Vorlage hinzufügen' : 'Ajouter un modèle'}>
                        <Upload className="w-3.5 h-3.5" />
                      </span>
                    </label>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleDeleteDocRequest(req.id, req.label)}
                      title={lang === 'de' ? 'Löschen' : 'Supprimer'}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add new document */}
            <div className="border-t pt-4 mt-4 space-y-3">
              <h4 className="font-medium text-sm">{lang === 'de' ? 'Dokument hinzufügen' : 'Ajouter un document'}</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{lang === 'de' ? 'Dokumentname' : 'Nom du document'} *</Label>
                  <Input
                    value={addDocLabel}
                    onChange={(e) => setAddDocLabel(e.target.value)}
                    placeholder={lang === 'de' ? 'Z.B.: B1 Zertifikat' : 'Ex: Certificat B1'}
                    className="h-9 text-sm"
                    data-testid="add-doc-label"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{lang === 'de' ? 'Akzeptiertes Format' : 'Format accepté'}</Label>
                  <Select value={addDocFormat} onValueChange={setAddDocFormat}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF</SelectItem>
                      <SelectItem value="word">Word</SelectItem>
                      <SelectItem value="image">{lang === 'de' ? 'Bild (JPG/PNG)' : 'Image (JPG/PNG)'}</SelectItem>
                      <SelectItem value="pdf,image">{lang === 'de' ? 'PDF oder Bild' : 'PDF ou Image'}</SelectItem>
                      <SelectItem value="tous">{lang === 'de' ? 'Alle Formate' : 'Tous formats'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{lang === 'de' ? 'Notizen für die/den Studierende/n (optional)' : "Notes pour l'étudiant (optionnel)"}</Label>
                <Input
                  value={addDocNotes}
                  onChange={(e) => setAddDocNotes(e.target.value)}
                  placeholder={lang === 'de' ? 'Z.B.: Füllen Sie dieses Dokument aus und senden Sie es unterschrieben und datiert zurück' : 'Ex: Remplissez ce document et renvoyez-le signé et daté'}
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{lang === 'de' ? 'Vorlage zum Herunterladen (optional)' : 'Modèle à télécharger (optionnel)'}</Label>
                <Input
                  type="file"
                  onChange={(e) => setAddDocTemplate(e.target.files?.[0] || null)}
                  className="h-9 text-sm"
                />
              </div>
              <Button
                onClick={handleAddDocRequest}
                disabled={!addDocLabel.trim() || addingDoc}
                size="sm"
                className="w-full"
                data-testid="add-doc-submit"
              >
                {addingDoc ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                {lang === 'de' ? 'Zur Liste hinzufügen' : 'Ajouter à la liste'}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};

// Dropzone Component
const DropzoneArea = ({ docKey, onDrop, isUploading, label = "Déposer le document" }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => onDrop(files, docKey),
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
  });

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
        isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
      }`}
    >
      <input {...getInputProps()} />
      {isUploading ? (
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Téléchargement en cours...</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <Upload className="w-8 h-8 text-muted-foreground" />
          <p className="text-sm font-medium">{label}</p>
          <p className="text-xs text-muted-foreground">
            PDF, JPG ou PNG • Max 10MB
          </p>
        </div>
      )}
    </div>
  );
};

export default DossierDetail;
